import React, { useEffect, useState } from 'react';

import { contactApis, dashboardContactApis } from '@apis/next/contactApis';
import infoIcon from '@assets/img/icons/info.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { QueryContainer } from '@components/QueryContainer';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { ContactFilter } from '@components/utilities/BookingInquiry/ContactFilter';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import {
  contactForm,
  ContactForm as ContactFormData,
  contactFormInitialValues,
} from '@models/Contact';
import { ContactSearchQuery } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings, useErrorMessage } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik, useField } from 'formik';

export const strings = createStrings({
  ja: {
    chooseConsigneePrompt: `${bookingInquiryStrings.consignee}を選択してください`,
    clientChooseConsignee: `${bookingInquiryStrings.consignee}一覧から選択する`,
    fillInWithNewConsigneeInfo: `手入力で変更する`,
    ok: '次へ',
    createConsigneeDialogTitle: 'CNEE（輸入者）情報を入力してください',
    consigneeInfoTitle: 'CNEE（輸入者）情報',
    chooseConsignee: 'CNEE一覧から選択する',
    aboutConsignee: () => (
      <>
        CNEE（輸入者）とは？
        <br />
        コンサイニー。B/Lに記載された荷受人のことをいう。 ConsigneeをCNEEと略すことが多い。
        <br />
        一般的には、輸入者のこと。　
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem',
  },
  dialog: {
    maxWidth: '960px !important',
  },
  contactItem: {
    ...common.flexColumn,
    gap: '0.875rem',
  },
  contactItemTitle: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
  },
  cneeInfoContainer: {
    ...common.flexInline,
    gap: '4rem',
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumn,
      gap: '1rem',
    },
  },
  receiverChooseOptionsContainer: {
    ...common.flexInline,
    gap: '20px',
  },
  infoTips: {
    ...common.textGray700,
    ...common.smallerText,
    ...common.flexInline,
    ...common.itemsStart,
    lineHeight: '1.125rem',
    minHeight: '4rem',
  },
}));

export const ConsigneeInfoInput: React.FC = () => {
  const { isDashboard, ownerId, ownerCompanyId } = useBookingCreateContext();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [shipperField] = useField<number | undefined>('shipper');
  const [, , receiverHelpers] = useField<number | undefined>('receiver');
  const [consigneeField, , consigneeHelpers] = useField<number | undefined>('consignee');
  const [consigneeContactField, , consigneeContactHelpers] = useField<ContactFormData | undefined>(
    'consignee_input',
  );
  const [, , receiverContactHelpers] = useField<ContactFormData | undefined>('receiver_input');

  const error = useErrorMessage('consignee');

  const [consigneeParams, setConsigneeParams] = useState<ContactSearchQuery>({});

  const consigeesSearchQueries = {
    ...consigneeParams,
    creator_id: isDashboard ? ownerId : undefined,
    contact_type: 'consignee',
    ...(shipperField.value
      ? {
          sort_by_related_to: shipperField.value,
        }
      : {
          ordering: ['-use_count'],
        }),
  } satisfies ContactSearchQuery;

  const consigneesQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact', { queries: consigeesSearchQueries })
    : contactApis.useGet('/contact', { queries: consigeesSearchQueries });

  const consigneeQueryParams = [
    {
      params: { id: consigneeField.value! },
    },
    {
      enabled: !!consigneeField.value,
    },
  ] as const;

  const consigneeQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact/:id', ...consigneeQueryParams)
    : contactApis.useGet('/contact/:id', ...consigneeQueryParams);

  const consigneesData = consigneesQuery.data;

  const initialConsigneeValues = consigneeContactField.value
    ? consigneeContactField.value
    : contactFormInitialValues;

  const [consigneeCreateDialogOpen, setConsigneeCreateDialogOpen] = useState(false);
  const [consigneeChooseDialogOpen, setConsigneeChooseDialogOpen] = useState(false);

  useEffect(() => {
    const companyContact = consigneesData?.find((c) => c.user === ownerCompanyId);
    if (companyContact?.english_name && !consigneeField.value) {
      consigneeHelpers.setValue(companyContact.id);
    }
  }, [consigneesData, ownerId]);

  const renderConsigneeChooseOptions = (change?: boolean) => {
    return (
      <div className={classes.receiverChooseOptionsContainer}>
        <Button
          data-testid="consignee-choose"
          color="primary"
          text={
            isDashboard
              ? strings.chooseConsignee
              : change
              ? formStrings.change
              : strings.clientChooseConsignee
          }
          size="small"
          shape="round"
          type="button"
          onClick={() => setConsigneeChooseDialogOpen(true)}
        />
        {isDashboard && <div>or</div>}
        {isDashboard && (
          <Button
            data-testid="consignee-create"
            color="primary"
            text={strings.fillInWithNewConsigneeInfo}
            size="small"
            shape="round"
            type="button"
            onClick={() => setConsigneeCreateDialogOpen(true)}
          />
        )}
      </div>
    );
  };

  const renderConsignee = () => (
    <>
      <div className={classes.cneeInfoContainer}>
        {consigneeContactField.value && <ContactDisplay contact={consigneeContactField.value} />}
        {consigneeQuery.data && <ContactDisplay contact={consigneeQuery.data} />}
      </div>
      <div style={{ height: 14 }} />

      {renderConsigneeChooseOptions(true)}
    </>
  );

  const renderChooseConsigneeDialog = () => (
    <ActionableDialog
      open={consigneeChooseDialogOpen}
      onClose={() => setConsigneeChooseDialogOpen(false)}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.chooseConsigneePrompt}</ActionableDialogTitle>
      <ActionableDialogContent>
        <QueryContainer style={{ height: 478 }} queries={[consigneesQuery]}>
          <ContactFilter
            contacts={consigneesData ?? []}
            title={bookingInquiryStrings.consignee}
            selectedIds={consigneeField.value ? [consigneeField.value] : []}
            onChangeSelectedIds={(ids) => {
              if (ids[ids.length - 1] !== consigneeField.value) {
                receiverHelpers.setValue(undefined);
                consigneeContactHelpers.setValue(undefined);
                receiverContactHelpers.setValue(undefined);
                consigneeHelpers.setTouched(true);
                consigneeHelpers.setValue(ids[ids.length - 1]);
              }
            }}
            onAddContact={() => window.open(url('/utilities/Contact/Consignees/AddConsignee'))}
            onEditContact={() => window.open(url('/utilities/Contact/Consignees/Consignees'))}
            onRefresh={() => {
              consigneesQuery.remove();
              consigneesQuery.refetch();
            }}
            query={consigneeParams}
            onChangeQuery={setConsigneeParams}
            actions={isDashboard ? 'none' : 'add-list'}
            isEnglishNameRequired
            isZipcodeRequired
            editUrl={(contact) =>
              (isDashboard
                ? contact.user && `${url('/admin/members/MemberDetails')}?id=${contact.user}`
                : contact.id &&
                  `${url('/utilities/Contact/Consignees/ConsigneeDetails')}?id=${contact.id}`) || ''
            }
          />
        </QueryContainer>
      </ActionableDialogContent>
      <ActionableDialogContentDivider />
      <div style={{ padding: '1rem 24px' }}>
        <Button
          color="primary"
          text={strings.ok}
          size="small"
          data-testid="close-contact-filter"
          disabled={!consigneeField.value}
          onClick={() => {
            setConsigneeChooseDialogOpen(false);
          }}
          style={{ width: '100%' }}
        />
      </div>
    </ActionableDialog>
  );

  const renderConsigneeCreateDialog = () => {
    return (
      <ActionableDialog
        open={consigneeCreateDialogOpen}
        onClose={() => setConsigneeCreateDialogOpen(false)}
        classes={{
          paper: classes.dialog,
        }}
        muiDialogProps={{
          disablePortal: false,
        }}
      >
        <Formik
          initialValues={initialConsigneeValues}
          validate={createValidate(contactForm)}
          onSubmit={async (values) => {
            consigneeHelpers.setValue(0);
            consigneeHelpers.setTouched(true);
            consigneeContactHelpers.setValue(values);
            consigneeContactHelpers.setTouched(true);
            receiverHelpers.setValue(undefined);
            setConsigneeCreateDialogOpen(false);
          }}
        >
          {({ handleSubmit }) => (
            <Form>
              <SubmittingOverlay />

              <ActionableDialogTitle textAlign="center">
                {strings.createConsigneeDialogTitle}
              </ActionableDialogTitle>
              <ActionableDialogContent>
                <ContactForm zipcodeRequired contactType="consignee" />
              </ActionableDialogContent>

              <ActionableDialogContentDivider />
              <div style={{ padding: '1rem 24px' }}>
                <Button
                  color="primary"
                  text={formStrings.ok}
                  size="small"
                  shape="round"
                  data-testid="close-contact-create"
                  onClick={() => {
                    handleSubmit();
                  }}
                  style={{ margin: '0 auto', padding: '2px 36px' }}
                />
              </div>
            </Form>
          )}
        </Formik>
      </ActionableDialog>
    );
  };

  return (
    <div className={clsx(classes.container)}>
      {consigneeField.value === undefined && renderConsigneeChooseOptions()}
      {consigneeField.value !== undefined && renderConsignee()}
      {error != null && (
        <>
          <VerticalSpace height={10} />
          <div className={clsx(classes.textError, classes.smallerText)}>{error ?? 'Error'}</div>
        </>
      )}
      {consigneeField.value === undefined && (
        <>
          <VerticalSpace height={10} />
          <div className={classes.infoTips}>
            <img src={infoIcon} style={{ height: 13.75, width: 13.75 }} alt="info" />
            <div style={{ width: 10 }} />
            {strings.aboutConsignee()}
          </div>
        </>
      )}
      {renderChooseConsigneeDialog()}
      {renderConsigneeCreateDialog()}
    </div>
  );
};
